import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Plus, Trash2, Pencil, Loader2, X, Upload, FileText, Check, ShoppingBag, DollarSign, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { AdminNav } from "./AdminContinueBuilding";
import {
  adminCheck, adminListStartups, adminCreateStartup, adminUpdateStartup,
  adminDeleteStartup, adminListStartupDocs, adminUploadStartupDoc, adminDeleteStartupDoc,
  fetchStartupOptions, adminSalesSummary,
} from "../lib/api";

const EMPTY = {
  name: "", category: "AI", platform: "SaaS", tagline: "", preview_url: "", tech: [], screens_count: 40,
  features_count: 30, badges: [], problem: "", solution: "", target_audience: "",
  value_prop: "", features: [], monetization: [], roadmap: [], published: true,
};
const BADGE_OPTS = ["Admin Dashboard", "Authentication", "Database Ready", "AI Ready"];

const listToStr = (a) => (a || []).join(", ");
const strToList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function AdminStartups() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [items, setItems] = useState([]);
  const [opts, setOpts] = useState({ categories: [], platforms: [], technologies: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // startup object or {} for new
  const [docsFor, setDocsFor] = useState(null); // startup for document manager
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminListStartups().then(setItems).catch(() => {}).finally(() => setLoading(false));
    adminSalesSummary().then(setSummary).catch(() => {});
  }, []);

  useEffect(() => {
    adminCheck().then((r) => {
      if (!r.admin) { setAuthorized(false); navigate("/"); return; }
      setAuthorized(true);
      fetchStartupOptions().then(setOpts).catch(() => {});
      load();
    }).catch(() => { setAuthorized(false); navigate("/"); });
  }, [navigate, load]);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await adminDeleteStartup(s.id).catch(() => {});
    toast.success("Startup deleted");
    load();
  };

  if (authorized === null) return <div className="min-h-screen flex items-center justify-center bg-[#0F0F0F] text-white"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <header className="bg-[#0F0F0F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-6">
          <div className="flex items-center gap-2"><Rocket size={20} className="text-[#D4AF37]" /><span className="font-display font-bold">LaunchBS Admin</span></div>
          <AdminNav active="startups" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0F0F0F]">Ready-to-Build Startups</h1>
            <p className="text-[#687280] text-sm mt-1">Manage listings and purchase documents. All startups are $250.</p>
          </div>
          <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#c5a030]" data-testid="admin-add-startup-btn"><Plus size={16} /> Add Startup</button>
        </div>

        {/* Sales totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="admin-sales-summary">
          <SummaryCard icon={ShoppingBag} label="Startups Sold" value={summary ? summary.startups_sold : "—"} testid="summary-startups-sold" />
          <SummaryCard icon={DollarSign} label="Startup Revenue" value={summary ? `$${summary.startup_revenue.toLocaleString()}` : "—"} testid="summary-startup-revenue" />
          <SummaryCard icon={CreditCard} label="Active Subscriptions" value={summary ? summary.subscriptions_active : "—"} sub={summary ? `${summary.subscriptions_total} total` : null} testid="summary-subscriptions" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#687280]"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid={`admin-startup-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[11px] font-semibold text-[#D4AF37] uppercase">{s.category}</p>
                    <h3 className="font-display font-bold text-[#0F0F0F]">{s.name}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.published ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>{s.published ? "Published" : "Draft"}</span>
                    {s.sold_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0F0F0F] text-[#D4AF37]" data-testid={`admin-startup-sold-${s.id}`}>
                        <Check size={11} /> SOLD · {s.sold_count}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-[#687280] mt-1 line-clamp-2">{s.tagline}</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEditing(s)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-gray-200 rounded-full py-2 hover:border-[#D4AF37]" data-testid={`admin-edit-startup-${s.id}`}><Pencil size={13} /> Edit</button>
                  <button onClick={() => setDocsFor(s)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-[#0F0F0F] text-white rounded-full py-2 hover:bg-[#1c1c1c]" data-testid={`admin-docs-startup-${s.id}`}><FileText size={13} /> Documents</button>
                  <button onClick={() => remove(s)} className="inline-flex items-center justify-center text-red-500 border border-gray-200 rounded-full w-9 hover:border-red-300" data-testid={`admin-delete-startup-${s.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing && <StartupForm startup={editing} opts={opts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {docsFor && <DocsManager startup={docsFor} onClose={() => setDocsFor(null)} />}
    </div>
  );
}

const StartupForm = ({ startup, opts, onClose, onSaved }) => {
  const isNew = !startup.id;
  const [f, setF] = useState({ ...EMPTY, ...startup });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      ...f, screens_count: Number(f.screens_count) || 0, features_count: Number(f.features_count) || 0,
    };
    try {
      if (isNew) await adminCreateStartup(payload);
      else await adminUpdateStartup(startup.id, payload);
      toast.success(isNew ? "Startup created" : "Startup updated");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-startup-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-[#0F0F0F]">{isNew ? "Add Startup" : "Edit Startup"}</h2>
          <button onClick={onClose}><X size={20} className="text-[#687280]" /></button>
        </div>
        <div className="space-y-3">
          <Row><Inp label="Startup Name" v={f.name} onC={(v) => set("name", v)} testid="sf-name" /></Row>
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Category" v={f.category} opts={opts.categories} onC={(v) => set("category", v)} />
            <Sel label="Platform" v={f.platform} opts={opts.platforms} onC={(v) => set("platform", v)} />
          </div>
          <Inp label="Short Description (tagline)" v={f.tagline} onC={(v) => set("tagline", v)} testid="sf-tagline" />
          <Inp label="Live Preview URL (Open Live Preview link)" v={f.preview_url || ""} onC={(v) => set("preview_url", v)} testid="sf-preview-url" />
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Screens" type="number" v={f.screens_count} onC={(v) => set("screens_count", v)} />
            <Inp label="Features count" type="number" v={f.features_count} onC={(v) => set("features_count", v)} />
          </div>
          <Inp label="Technology (comma separated)" v={listToStr(f.tech)} onC={(v) => set("tech", strToList(v))} />
          <div>
            <label className="text-xs font-semibold text-[#374151]">Badges</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {BADGE_OPTS.map((b) => {
                const on = f.badges.includes(b);
                return <button key={b} onClick={() => set("badges", on ? f.badges.filter((x) => x !== b) : [...f.badges, b])} className={`text-xs px-3 py-1.5 rounded-full border ${on ? "border-[#D4AF37] bg-[#FBF7EA] text-[#8a6d1a]" : "border-gray-200 text-[#374151]"}`}>{b}</button>;
              })}
            </div>
          </div>
          <Area label="Problem" v={f.problem} onC={(v) => set("problem", v)} />
          <Area label="Solution" v={f.solution} onC={(v) => set("solution", v)} />
          <Inp label="Target Audience" v={f.target_audience} onC={(v) => set("target_audience", v)} />
          <Inp label="Value Proposition" v={f.value_prop} onC={(v) => set("value_prop", v)} />
          <Inp label="Features (comma separated)" v={listToStr(f.features)} onC={(v) => set("features", strToList(v))} />
          <Inp label="Monetization (comma separated)" v={listToStr(f.monetization)} onC={(v) => set("monetization", strToList(v))} />
          <Inp label="Roadmap (comma separated)" v={listToStr(f.roadmap)} onC={(v) => set("roadmap", strToList(v))} />
          <label className="flex items-center gap-2 text-sm text-[#374151]"><input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} /> Published (visible in marketplace)</label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 rounded-full border border-gray-200 py-2.5 text-sm font-semibold">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2.5 text-sm font-bold hover:bg-[#c5a030] disabled:opacity-60 inline-flex items-center justify-center gap-1.5" data-testid="sf-save">{saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
};

const DocsManager = ({ startup, onClose }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => { adminListStartupDocs(startup.id).then(setDocs).catch(() => {}); }, [startup.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await adminUploadStartupDoc(startup.id, file); toast.success("Uploaded"); load(); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (d) => { await adminDeleteStartupDoc(startup.id, d.id).catch(() => {}); load(); };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-docs-manager">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-xl font-bold text-[#0F0F0F]">Documents — {startup.name}</h2>
          <button onClick={onClose}><X size={20} className="text-[#687280]" /></button>
        </div>
        <p className="text-sm text-[#687280] mb-4">Buyers download these files after purchasing to upload into their AI builder.</p>
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-2xl py-6 cursor-pointer hover:border-[#D4AF37]" data-testid="admin-doc-upload">
          {uploading ? <Loader2 className="animate-spin" /> : <Upload size={18} className="text-[#D4AF37]" />}
          <span className="text-sm font-semibold text-[#374151]">{uploading ? "Uploading…" : "Upload a document"}</span>
          <input ref={fileRef} type="file" className="hidden" onChange={upload} />
        </label>
        <div className="mt-4 space-y-2">
          {docs.length === 0 ? <p className="text-sm text-[#687280] text-center py-4">No documents yet.</p> : docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between bg-[#F7F7F7] rounded-xl p-3">
              <div className="flex items-center gap-2 min-w-0"><FileText size={15} className="text-[#D4AF37] shrink-0" /><span className="text-sm truncate">{d.filename}</span></div>
              <button onClick={() => del(d)} className="text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Row = ({ children }) => <div>{children}</div>;
const SummaryCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm flex items-center gap-4" data-testid={testid}>
    <div className="w-11 h-11 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><Icon size={20} /></div>
    <div>
      <p className="text-xs font-semibold text-[#687280] uppercase tracking-wide">{label}</p>
      <p className="font-display text-2xl font-extrabold text-[#0F0F0F] leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-[#9aa1ab]">{sub}</p>}
    </div>
  </div>
);
const Inp = ({ label, v, onC, type = "text", testid }) => (
  <div>
    <label className="text-xs font-semibold text-[#374151]">{label}</label>
    <input type={type} value={v} onChange={(e) => onC(e.target.value)} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid={testid} />
  </div>
);
const Area = ({ label, v, onC }) => (
  <div>
    <label className="text-xs font-semibold text-[#374151]">{label}</label>
    <textarea rows={2} value={v} onChange={(e) => onC(e.target.value)} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37] resize-none" />
  </div>
);
const Sel = ({ label, v, opts, onC }) => (
  <div>
    <label className="text-xs font-semibold text-[#374151]">{label}</label>
    <select value={v} onChange={(e) => onC(e.target.value)} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37] bg-white">
      {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
